/**
 * KONTRAST KOLORÓW NA CIEMNYM MOTYWIE — jedno źródło arytmetyki dla palet, brandingu i zapadek (O1, 2026-09-13).
 *
 * [zgłoszenie właściciela] „w dark motyw zależnie od koloru aplikacji jest tak, że często te przyciski
 * w ciemnych kolorach tła są tak ciemne, że ich nie widać, trzeba to kontrastować."
 *
 * PRZYCZYNA: kolor wiodący (`--accent`) był TEN SAM w jasnym i ciemnym motywie. Paleta, która jest
 * ciemna z natury (granat — domyślna, noc, czarny, grafit „białej"), leżała w ciemnym motywie na ciemnej
 * karcie z kontrastem 1,0–2,8:1 — i jako wypełnienie przycisku, i jako kolor tekstu (licznik „1 z 1",
 * odznaki, linki). Kolor z panelu Branding nie miał żadnej wersji ciemnej.
 *
 * ZASADA: ten sam odcień (barwa i nasycenie w HSL zostają), a JASNOŚĆ rośnie tylko tyle, ile trzeba,
 * żeby na KAŻDEJ ciemnej powierzchni aplikacji:
 *   1. kolor jako TEKST miał >= 4,5:1 (WCAG 2.1 AA) — a więc jako wypełnienie na pewno >= 3:1,
 *   2. kolor jako tekst na WŁASNYM bladym podkładzie (odznaka `rgba(akcent, .10–.15)`) miał >= 4,5:1,
 *   3. etykieta NA wypełnieniu (lepszy z dwóch atramentów) miała >= 4,5:1.
 * Kolor, który już to spełnia, wraca bez zmian. Wynik jest liczony, nie dobierany na oko, więc działa
 * dla KAŻDEGO koloru — także takiego, którego żadna paleta dziś nie ma (branding spółki).
 *
 * Arytmetyka luminancji i kontrastu jest dosłownie z WCAG 2.1 (sRGB, próg 0,04045).
 */

/** Powierzchnie wszystkich wariantów ciemnego motywu (global.css: dark, dark + wysoki kontrast, dark + czytnik).
 *  Zapadka `styles/__tests__/kontrastMotywuCiemnego.o1.test.js` sprawdza, że lista pokrywa arkusz. */
export const CIEMNE_POWIERZCHNIE = Object.freeze([
    "#121315", "#17181b", "#1e2024", "#26282d",
    "#000000", "#0a0a0a", "#141414", "#1e1e1e",
    "#111111",
]);

export const ATRAMENT_JASNY = "#ffffff";
/** Ciemny atrament = tło strony ciemnego motywu: napis „wycięty" w wypełnieniu. */
export const ATRAMENT_CIEMNY = "#121315";
/** Ciemny atrament palet w motywie jasnym (styles/accent.css). */
export const ATRAMENT_CIEMNY_JASNEGO_MOTYWU = "#241c08";

export const PROGI = Object.freeze({ tekst: 4.5, etykieta: 4.5, wypelnienie: 3, odznaka: 4.5 });
/** Krycia bladych podkładów, jakimi moduły kładą akcent pod własny tekst (`rgba(var(--accent-rgb), .1)` / `--accent-soft`). */
export const KRYCIA_PODKLADU = Object.freeze([0.1, 0.15]);

/** `#rgb` / `#rrggbb` → [r, g, b]; null dla każdego innego zapisu — nie zgadujemy. */
export const naRgb = (kolor) => {
    if (Array.isArray(kolor) && kolor.length === 3) return kolor.map((v) => Math.max(0, Math.min(255, Math.round(v))));
    if (typeof kolor !== "string") return null;
    const m = kolor.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return null;
    const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

export const naHex = (rgb) => "#" + naRgb(rgb).map((v) => v.toString(16).padStart(2, "0")).join("");

/** „r, g, b" — format tokenów `--accent-rgb`. */
export const naSkladowe = (kolor) => {
    const rgb = naRgb(kolor);
    return rgb ? rgb.join(", ") : null;
};

const kanal = (v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const luminancja = (kolor) => {
    const [r, g, b] = naRgb(kolor);
    return 0.2126 * kanal(r) + 0.7152 * kanal(g) + 0.0722 * kanal(b);
};

export const kontrast = (a, b) => {
    const la = luminancja(a);
    const lb = luminancja(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** Kolor półprzezroczysty złożony z tłem tak, jak składa go przeglądarka (w sRGB). */
export const zlozony = (kolor, krycie, tlo) => {
    const f = naRgb(kolor);
    const t = naRgb(tlo);
    return naHex(f.map((v, i) => v * krycie + t[i] * (1 - krycie)));
};

/** Z podanych atramentów ten, który na danym wypełnieniu daje WYŻSZY kontrast (mierzony, nie szacowany progiem). */
export const lepszyAtrament = (tlo, atramenty = [ATRAMENT_JASNY, ATRAMENT_CIEMNY]) =>
    atramenty.reduce((naj, a) => (kontrast(tlo, a) > kontrast(tlo, naj) ? a : naj));

const naHsl = ([r, g, b]) => {
    const [rr, gg, bb] = [r / 255, g / 255, b / 255];
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rr) h = (gg - bb) / d + (gg < bb ? 6 : 0);
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    return [h / 6, s, l];
};

const zHsl = ([h, s, l]) => {
    if (s === 0) return naRgb([l * 255, l * 255, l * 255]);
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const f = (t) => {
        let x = t;
        if (x < 0) x += 1;
        if (x > 1) x -= 1;
        if (x < 1 / 6) return p + (q - p) * 6 * x;
        if (x < 1 / 2) return q;
        if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
        return p;
    };
    return naRgb([f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255]);
};

/** Czy kolor spełnia WSZYSTKIE trzy role (tekst, tekst na własnym podkładzie, wypełnienie z etykietą) na podanych powierzchniach. */
export const czytelnyNaPowierzchniach = (kolor, powierzchnie = CIEMNE_POWIERZCHNIE) => {
    if (!naRgb(kolor)) return false;
    for (const p of powierzchnie) {
        if (kontrast(kolor, p) < PROGI.tekst) return false;
        for (const k of KRYCIA_PODKLADU) {
            if (kontrast(kolor, zlozony(kolor, k, p)) < PROGI.odznaka) return false;
        }
    }
    return kontrast(kolor, lepszyAtrament(kolor)) >= PROGI.etykieta;
};

/** Ten sam odcień (H i S w HSL), jasność podniesiona do NAJMNIEJSZEJ wartości spełniającej wszystkie role. */
export const dociagnijNaCiemne = (kolor, powierzchnie = CIEMNE_POWIERZCHNIE) => {
    const rgb = naRgb(kolor);
    if (!rgb) return null;
    const wejscie = naHex(rgb);
    if (czytelnyNaPowierzchniach(wejscie, powierzchnie)) return wejscie;
    const [h, s, l] = naHsl(rgb);
    // Zgrubnie krokiem 0,02 do pierwszej jasności, która przechodzi, potem dokładnie połowieniem
    // przedziału — bez zakładania monotoniczności poza tym wąskim oknem. Zwracany jest ZAWSZE kolor sprawdzony.
    let dol = l;
    let gora = null;
    for (let x = l + 0.02; x <= 1.0001; x += 0.02) {
        const probka = naHex(zHsl([h, s, Math.min(1, x)]));
        if (czytelnyNaPowierzchniach(probka, powierzchnie)) { gora = Math.min(1, x); break; }
        dol = Math.min(1, x);
    }
    if (gora === null) return czytelnyNaPowierzchniach(ATRAMENT_JASNY, powierzchnie) ? ATRAMENT_JASNY : null;
    for (let i = 0; i < 14; i += 1) {
        const srodek = (dol + gora) / 2;
        if (czytelnyNaPowierzchniach(naHex(zHsl([h, s, srodek])), powierzchnie)) gora = srodek;
        else dol = srodek;
    }
    return naHex(zHsl([h, s, gora]));
};

/**
 * Komplet barw akcentu dla ciemnego motywu z DOWOLNEGO koloru wejściowego.
 * `hover` jest o stopień jaśniejszy (jasne wypełnienie na ciemnym tle rozjaśnia się pod kursorem);
 * kolor już prawie biały ciemnieje o stopień — o ile nadal spełnia wszystkie role.
 */
export const kolorNaCiemnym = (kolor, powierzchnie = CIEMNE_POWIERZCHNIE) => {
    const akcent = dociagnijNaCiemne(kolor, powierzchnie);
    if (!akcent) return null;
    const [h, s, l] = naHsl(naRgb(akcent));
    let hover = akcent;
    const jasniejszy = naHex(zHsl([h, s, Math.min(0.97, l + 0.08)]));
    const ciemniejszy = naHex(zHsl([h, s, Math.max(0, l - 0.08)]));
    if (l + 0.08 <= 0.97 && czytelnyNaPowierzchniach(jasniejszy, powierzchnie)) hover = jasniejszy;
    else if (czytelnyNaPowierzchniach(ciemniejszy, powierzchnie)) hover = ciemniejszy;
    const naAkcencie = lepszyAtrament(akcent);
    return {
        wejscie: naRgb(kolor) ? naHex(naRgb(kolor)) : null,
        akcent,
        hover,
        naAkcencie,
        naHover: lepszyAtrament(hover),
        zmieniony: akcent !== naHex(naRgb(kolor)),
    };
};

/**
 * Deklaracje tokenów akcentu dla ciemnego motywu — w tej kolejności trafiają do `styles/accentCiemny.css`
 * (generowany z tej funkcji; zapadka porównuje plik z wynikiem) i do reguły brandingu w BrandingContext.
 * @returns {Array<[string, string]>} pary [nazwa tokenu, wartość]
 */
export const tokenyAkcentuCiemnego = (kolor, powierzchnie = CIEMNE_POWIERZCHNIE) => {
    const k = kolorNaCiemnym(kolor, powierzchnie);
    if (!k) return null;
    return [
        ["--accent", k.akcent],
        ["--accent-hover", k.hover],
        ["--accent-rgb", naSkladowe(k.akcent)],
        ["--accent-on", k.naAkcencie],
        ["--accent-on-rgb", naSkladowe(k.naAkcencie)],
        ["--accent-tile", k.akcent],
        ["--accent-tile-rgb", naSkladowe(k.akcent)],
        ["--accent-tile-on", k.naAkcencie],
        ["--accent-tile-on-rgb", naSkladowe(k.naAkcencie)],
        ["--accent-soft", "rgba(var(--accent-rgb), 0.15)"],
        ["--accent-border", "rgba(var(--accent-rgb), 0.36)"],
    ];
};

/** Pary tokenów → treść bloku CSS („--a: x; --b: y;"). */
export const jakoDeklaracje = (pary) => pary.map(([n, v]) => `${n}: ${v};`).join(" ");
